import React, { useEffect, useState } from 'react';
import { Button, Progress } from 'antd';
import { observer } from 'mobx-react';
import style from './style.module.scss';
import { ButtonFileInput } from '../button-file-input';
import { ImageHolder } from '../image-holder';
import { NotificationManager } from '../notification-manager';
import { useRppAdStore } from '../../contexts/mobx';
import {
    RppAssetType,
    RppUploadError,
    acceptAttrFor,
    getAssetConstraint,
    uploadFileToS3,
    validateAssetFile,
} from '../../utils/rpp-assets';

interface RppAssetInputProps {
    assetType: RppAssetType;
    label: string;
    /** The stored asset key, or empty when nothing is uploaded yet. */
    value: string;
    /** Initial signed preview URL (e.g. from an ad detail response). */
    initialPreviewUrl?: string | null;
    onUploaded: (key: string) => void;
    onRemove?: () => void;
    onUploadingChange?: (uploading: boolean) => void;
    disabled?: boolean;
}

function describeConstraint(assetType: RppAssetType): string {
    const { allowedContentTypes, maxBytes } = getAssetConstraint(assetType);
    const extensions = allowedContentTypes
        .map((type) => type.split('/')[1].toUpperCase())
        .join(', ');
    const maxMiB = Math.round(maxBytes / (1024 * 1024));
    return `Allowed: ${extensions}. Max ${maxMiB} MiB.`;
}

export const RppAssetInput = observer((props: RppAssetInputProps) => {
    const {
        assetType,
        label,
        value,
        initialPreviewUrl,
        onUploaded,
        onRemove,
        onUploadingChange,
        disabled,
    } = props;

    const store = useRppAdStore();
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState<string | null>(
        initialPreviewUrl ?? null
    );

    useEffect(() => {
        setPreviewUrl(initialPreviewUrl ?? null);
    }, [initialPreviewUrl]);

    const setUploading = (uploading: boolean) => {
        setIsUploading(uploading);
        onUploadingChange?.(uploading);
    };

    const uploadButtonText =
        assetType === RppAssetType.AD_VIDEO ? 'Upload video' : 'Upload image';

    const handleFile = async (file: File) => {
        const validationError = validateAssetFile(file, assetType);
        if (validationError) {
            NotificationManager.showError(validationError);
            return;
        }

        setUploading(true);
        setProgress(0);
        try {
            const target = await store.createUploadTarget({
                assetType,
                contentType: file.type,
                contentLength: file.size,
            });
            if (!target) {
                // The store already surfaced the API error.
                return;
            }

            await uploadFileToS3(target, file, setProgress);

            // Best-effort preview; a failure here does not invalidate the upload.
            const signedUrl = await store.getSignedUrl(target.key);
            setPreviewUrl(signedUrl);

            onUploaded(target.key);
            NotificationManager.showSuccess('File uploaded.');
        } catch (error) {
            const message =
                error instanceof RppUploadError
                    ? error.message
                    : 'Upload failed. Please try again.';
            NotificationManager.showError(message);
        } finally {
            setUploading(false);
        }
    };

    const loadPreview = async () => {
        if (!value) {
            return;
        }
        const signedUrl = await store.getSignedUrl(value);
        setPreviewUrl(signedUrl);
    };

    const renderPreview = () => {
        if (!previewUrl) {
            return (
                <Button size="small" onClick={loadPreview}>
                    Load preview
                </Button>
            );
        }
        if (assetType === RppAssetType.AD_VIDEO) {
            return (
                <video
                    src={previewUrl}
                    controls
                    className={style.video_preview}
                />
            );
        }
        return <ImageHolder src={previewUrl} size={120} />;
    };

    return (
        <div className={style.asset_input}>
            <span className={style.label}>{label}</span>

            {isUploading ? (
                <div className={style.progress_wrap}>
                    <Progress percent={progress} />
                    <span className={style.hint}>Uploading…</span>
                </div>
            ) : value ? (
                <div className={style.uploaded}>
                    {renderPreview()}
                    <code className={style.key}>{value}</code>
                    <div className={style.actions}>
                        <ButtonFileInput
                            accept={acceptAttrFor(assetType)}
                            onFileSelected={handleFile}
                            disabled={disabled}
                            text="Replace"
                        />
                        {onRemove && (
                            <Button
                                danger
                                onClick={onRemove}
                                disabled={disabled}
                            >
                                Remove
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className={style.empty}>
                    <ButtonFileInput
                        accept={acceptAttrFor(assetType)}
                        onFileSelected={handleFile}
                        disabled={disabled}
                        text={uploadButtonText}
                    />
                    <span className={style.hint}>
                        {describeConstraint(assetType)}
                    </span>
                </div>
            )}
        </div>
    );
});
